import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import RecentActivityCard from '../../shared/RecentActivityCard';
import { fetchEntityAuditLog } from '../../../store/actions/AuditLogActions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { hideModal, showModal } from '../../../store/actions/modalActions';
import { showSidePanel } from '../../../store/actions/sidePanelActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import apis from '../../../utils/apis';
import FileUploader from '../../shared/TailwindComponents/FileUploader';
import {
  PiArrowLeftLight,
  PiPencilLight,
  PiXLight,
  PiTrashLight,
  PiPlusCircleLight,
  PiSpinnerLight,
  PiTextAlignLeftLight,
  PiCircleDashedLight,
  PiUsersLight,
  PiCalendarBlankLight,
  PiClockClockwiseLight,
  PiClockCounterClockwiseLight,
  PiTagLight,
} from 'react-icons/pi';
import Button from '../../shared/TailwindComponents/Button';
import Badge from '../../shared/TailwindComponents/Badge';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import ProfileImage from '../../Header/ProfileImage';
import DetailPageSkeleton from '../Shared/DetailPageSkeleton';
import { LayoutContext } from '../../../contexts/LayoutContext';
import { fetchPracticeAreas } from '../../../store/actions/DropdownActions';
import {
  fetchTeamDetails,
  updateTeam,
  addTeamMember,
  removeTeamMember,
  fetchTeamFeed,
  postTeamFeedEvent,
  deleteTeam,
} from '../../../store/actions/organization/teamActions';
import MemberSelectionModal from '../Shared/MemberSelectionModal';
import FeedComponent from '../Shared/FeedComponent';
import DetailFrame from '../../shared/Frames/DetailFrame';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import DocumentsTab from './DocumentsTab';
import CollaboratorSelector from '../../shared/CollaboratorSelector';
import MembersSection from '../Shared/MembersSection';
import { formatDate, formatDateTime } from '../../../utils/functions';
import AuditLogPanel from '../../AuditLog/AuditLogPanel';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, teamDetailTourSteps } from '../../shared/FeatureTour';

interface Team {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  member_count: number;
  members: Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role?: string;
    firm_role?: string;
    joined_at?: string;
    profile_picture?: string | null;
  }>;
  created_at?: string;
  department?: string;
  practice_area?: string;
  practice_areas?: Array<{ id: number; name: string }>;
  practice_area_ids?: number[];
}

interface TeamDetailParams {
  teamId: string;
}

const TeamDetail: React.FC = () => {
  const { t } = useTranslation();
  const { teamId } = useParams<TeamDetailParams>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { setIsContentScrollable } = useContext(LayoutContext);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const practiceAreas = useSelector((state: RootState) => state.dropdown.dropdowns.practiceAreas) || [];

  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'Details' | 'feed' | 'matters' | 'documents'>('Details');
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [feed, setFeed] = useState<any[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [isUploadingDocs, setIsUploadingDocs] = useState(false);
  const [uploadingDocCount, setUploadingDocCount] = useState(0);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'team-detail',
    steps: teamDetailTourSteps,
    readyToShow: !loading && !!team,
    autoStartDelay: 600,
  });

  const [isEditingTeamInfo, setIsEditingTeamInfo] = useState(false);
  const [editedTeamFields, setEditedTeamFields] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);
  const [collaborators, setCollaborators] = useState<any[]>([]);

  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  useEffect(() => {
    dispatch(fetchPracticeAreas());
  }, [dispatch]);

  const practiceAreaOptions = practiceAreas.map((area: any) => ({
    value: String(area.value || area.id),
    label: String(area.label || area.name),
  }));

  const loadTeam = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await dispatch(fetchTeamDetails(teamId) as any);
      if (result) {
        setTeam(result);
        setEditedTeamFields(result);
      } else {
        setError('Failed to load team');
      }
    } catch (err) {
      setError('Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [teamId, dispatch]);

  const handleTeamFieldChange = (field: string, value: any) => {
    setEditedTeamFields((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSaveTeam = async () => {
    if (!team?.id) return;
    setIsSaving(true);
    try {
      await dispatch(updateTeam(team.id, {
        name: editedTeamFields.name,
        description: editedTeamFields.description,
        practice_area_ids: (editedTeamFields.practice_area_ids || []).map(Number),
      }));
      setIsEditingTeamInfo(false);
      await loadTeam();
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Team updated successfully'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (err) {
      console.error('Error saving team:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to save team'),
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingTeamInfo(false);
    setEditedTeamFields(team || {});
  };

  const handleAddCollaborators = async () => {
    if (collaborators.length === 0) return;
    setIsSaving(true);
    try {
      for (const collab of collaborators) {
        const memberId = collab.collaborator_uuid || String(collab.user_id || '');
        if (!memberId) continue;
        await dispatch(addTeamMember(teamId, memberId, collab.access === 'edit' ? 'member' : 'member'));
      }
      setCollaborators([]);
      await loadTeam();
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Members added successfully'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (err) {
      console.error('Error adding collaborators:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to add members'),
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setIsSaving(false);
    }
  };


  const loadFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const result = await dispatch(fetchTeamFeed(teamId) as any);
      if (Array.isArray(result)) {
        setFeed(result);
      }
    } finally {
      setFeedLoading(false);
    }
  }, [teamId, dispatch]);

  useEffect(() => {
    if (!teamId) return;
    loadTeam();
    loadFeed();
  }, [teamId, loadTeam, loadFeed]);

  const handleAddMember = async (member: any) => {
    try {
      await dispatch(addTeamMember(teamId, member.id, 'member'));
      await loadTeam();
    } catch (err) {
      console.error('Error adding member:', err);
    }
  };

  const handleRemoveMember = (memberId: string) => {
    dispatch(showModal({
      type: 'confirmation',
      message: t('Remove Member'),
      subMsg: t('This member will be removed from the team and lose access to all team resources.'),
      icon:  <PiTrashLight className='w-6 h-6 text-red-500' />,
      isDest: true,
      showModal: true,
      onConfirm: async () => {
        try {
          await dispatch(removeTeamMember(teamId, memberId));
          await loadTeam();
        } catch (err) {
          console.error('Error removing member:', err);
        }
        dispatch(hideModal())
      },
    }));
  };

  const handleSetLead = async (memberId: string) => {
    try {
      await apis({
        method: 'PUT',
        url: `/org-people/teams/${teamId}/members/${memberId}`,
        data: { role: 'lead' },
      });
      await loadTeam();
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Lead updated successfully'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (err) {
      console.error('Error setting lead:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to update lead'),
        modalType: 'error',
        duration: 3000,
      }));
    }
  };


  const handlePostFeedEvent = async (eventData: { event_type: string; message: string; metadata?: any }) => {
    try {
      await dispatch(postTeamFeedEvent(teamId, eventData));
      await loadFeed();
    } catch (err) {
      console.error('Error posting feed event:', err);
    }
  };

  const handleAddComment = async (eventId: string, comment: string) => {
    try {
      if (!team?.id) {
        throw new Error('Team ID not available');
      }
      await apis.post(`/org-people/teams/${team.id}/feed/${eventId}/comments`, { message: comment });
      await loadFeed();
    } catch (err) {
      console.error('Error adding comment:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      dispatch(showFeedbackModal({
        showModal: true,
        message: `Comment failed: ${errorMessage}`,
        modalType: 'error',
        duration: 5000,
      }));
    }
  };

  const handleDeleteTeam = () => {
    dispatch(showModal({
      type: 'confirmation',
      message: t('Delete Team'),
      subMsg: t('This team and all associated data will be permanently deleted. This action cannot be undone.'),
      icon: <PiTrashLight className='w-6 h-6 text-red-500' />,
      isDest: true,
      showModal: true,
      onConfirm: async () => {
        if (!team?.id) return;
        const result = await dispatch(deleteTeam(team.id) as any);
        dispatch(hideModal());
        if (result) {
          history.push('/teams');
        }
      },
    }));
  };

  const handleUploadDocument = async (files: File[]) => {
    setIsUploadingDocs(true);
    setUploadingDocCount(files.length);
    dispatch(hideModal());
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }

      const response = await apis.post(
        `/org-people/teams/${teamId}/documents/bulk-upload`,
        formData
      );

      if (response?.data?.success) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: response.data.message || t('Documents uploaded successfully'),
          modalType: 'success',
          duration: 3000,
        }));
      }

      await loadDocuments();
      await loadTeam();
      await loadFeed();
    } catch (err) {
      console.error('Error uploading document:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: `Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setIsUploadingDocs(false);
      setUploadingDocCount(0);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await apis.delete(`/org-people/teams/${teamId}/documents/${documentId}`);
      await loadDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: `Delete failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
        modalType: 'error',
        duration: 3000,
      }));
    }
  };

  const handleBulkDeleteDocuments = async (documentIds: string[]) => {
    await apis.post(`/org-people/teams/${teamId}/documents/bulk-delete`, { document_ids: documentIds });
    await loadDocuments();
    await loadTeam();
  };

  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    try {
      const response = await apis.get(`/org-people/teams/${teamId}/documents`);
      setDocuments(Array.isArray(response.data.documents) ? response.data.documents : []);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (activeTab === 'documents') {
      loadDocuments();
    }
    if (activeTab === 'feed') {
      loadFeed();
    }
  }, [activeTab, loadDocuments, loadFeed]);

  const tabs = [
    { label: t('Details'), value: 'Details' },
    { label: t('Feed'), value: 'feed' },
    { label: t('Matters'), value: 'matters', disabled: true, tooltip: t('Coming soon') },
    { label: t('Documents'), value: 'documents' },
  ];


  if (loading) {
    return (
      <DetailFrame
        breadcrumbs={[]}
        dropdownActions={[]}
        subscriptionActive={true}
        showBackButton={true}
      >
        <DetailPageSkeleton />
      </DetailFrame>
    );
  }

  if (error || !team) {
    return (
      <DetailFrame
        breadcrumbs={[]}
        dropdownActions={[]}
        subscriptionActive={true}
        showBackButton={true}
      >
        <div className="text-center py-12">
          <p className="text-red-600 dark:text-red-400 mb-4">{error || t('Team not found')}</p>
          <Button onClick={() => history.push('/teams')}>
            <PiArrowLeftLight className="w-4 h-4 mr-2" />
            {t('Back to Teams')}
          </Button>
        </div>
      </DetailFrame>
    );
  }



  const handleOpenDocumentUploadModal = () => {
    dispatch(showModal({
      type: 'uploadAttachment',
      message: t('Upload Team Document'),
      icon: <PiPlusCircleLight className='w-6 h-6' />,
      showModal: true,
      onConfirm: handleUploadDocument,
    }));
  };

  const memberAvatars = team?.members && team.members.length > 0 ? (
    <div className="flex items-center gap-3">
      <div className="flex -space-x-1.5">
        {team.members.slice(0, 4).map((member, idx) => (
          <div
            key={member.id}
            className="relative hover:z-20 transition-all"
            style={{ zIndex: 50 - idx }}
          >
            <ProfileImage
              profilePicture={member.profile_picture || null}
              email={member.email}
              initials={`${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase()}
              size="w-6 h-6"
              firstName={member.first_name}
              lastName={member.last_name}
              className="ring-1.5 ring-white dark:ring-gray-900"
            />
          </div>
        ))}
        {team.members.length > 4 && (
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 ring-1.5 ring-white dark:ring-gray-900 text-xs font-semibold text-gray-600 dark:text-gray-300">
            +{team.members.length - 4}
          </div>
        )}
      </div>
    </div>
  ) : null;

  // Document upload button for Documents tab
  const documentTabActions = activeTab === 'documents' ? (
    <Button
      size="small"
      onClick={handleOpenDocumentUploadModal}
      className="ml-auto mr-2"
    >
      <PiPlusCircleLight className="w-4 h-4 mr-1" />
      {t('Add Document')}
    </Button>
  ) : null;

  return (
    <DetailFrame
      breadcrumbs={[
        { name: t('Teams'), href: '/teams', current: false },
        { name: team?.name || t('Team'), href: `/teams/${teamId}`, current: true },
      ]}
      dropdownActions={[
        {
          label: t('Activity log'),
          icon: <PiClockCounterClockwiseLight className='w-4 h-4' />,
          onClick: () => setShowAuditLog(prev => !prev),
        },
        {
          label: t('Delete'),
          icon: <PiTrashLight className='w-4 h-4' />,
          onClick: handleDeleteTeam,
        },
      ]}
      subscriptionActive={true}
      showBackButton={true}
      additionalHeaderElements={memberAvatars}
    >
      <div className="flex flex-col h-full w-full">
        {/* Tabs - Static at top */}
        <div data-tour-id="team-detail-tabs" className="flex-shrink-0 bg-background-light dark:bg-background-dark border-b border-gray-200 dark:border-gray-700 mt-2">
          <div className="flex items-center justify-between">
            <FilterBarWithTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabClick={(val) => setActiveTab(val as any)}
              noParentHeight
              noParentPadding={true}
            />
            {documentTabActions}
          </div>
        </div>

        {/* Scrollable Content */}
        <motion.div
          data-tour-id="team-detail-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`flex-1 flex flex-col min-h-0 mt-2 ${activeTab !== 'feed' ? 'overflow-y-auto custom-scrollbar' : ''}`}
        >
          {activeTab === 'Details' && (
            <div className="max-h-[calc(100vh-100px)] overflow-y-auto custom-scrollbar-main px-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

                {/* ── Left Column ── */}
                <div className="lg:col-span-2 space-y-5">

                  {/* ── Description Card ── */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                    <div className="flex justify-between items-center px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
                      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                        <PiTextAlignLeftLight className="h-4 w-4" />
                        <span className="text-xs font-medium">{t('Description')}</span>
                      </div>
                      {!isEditingTeamInfo && (
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => setIsEditingTeamInfo(true)}
                          icon={<PiPencilLight className="h-3.5 w-3.5" />}
                        >
                          {t('Edit')}
                        </Button>
                      )}
                    </div>

                    {isEditingTeamInfo ? (
                      <div className="p-5 space-y-4">
                        <Input
                          type="text"
                          label={t('Team Name')}
                          placeholder={t('Enter team name')}
                          value={editedTeamFields.name || ''}
                          onChange={(e) => handleTeamFieldChange('name', e.target.value)}
                          required
                          className="w-full"
                        />
                        <Textarea
                          label={t('Description')}
                          placeholder={t('Enter team description')}
                          value={editedTeamFields.description || ''}
                          onChange={(e) => handleTeamFieldChange('description', e.target.value)}
                          className="w-full"
                          id="team-description"
                        />
                        <Dropdown
                          label={t('Practice Areas')}
                          options={practiceAreaOptions}
                          value={(editedTeamFields.practice_area_ids || []).map(String)}
                          onChange={(vals) => handleTeamFieldChange('practice_area_ids', Array.isArray(vals) ? vals.map(Number) : [Number(vals)])}
                          multiple
                          fullWidth
                          placeholder={t('Select practice areas')}
                        />

                        <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700/60">
                          <Button
                            variant="neutral"
                            size="small"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                          >
                            {t('Cancel')}
                          </Button>
                          <Button
                            variant="primary"
                            size="small"
                            onClick={handleSaveTeam}
                            disabled={isSaving}
                            icon={isSaving ? <PiSpinnerLight className="h-4 w-4 animate-spin" /> : undefined}
                          >
                            {isSaving ? t('Saving...') : t('Save Changes')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-5">
                        {team.description ? (
                          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap max-w-prose">
                            {team.description}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                            {t('No description provided')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Metadata Grid ── */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">

                    {/* Status */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
                      <div className="flex items-center gap-1.5 mb-2">
                        <PiCircleDashedLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Status')}</span>
                      </div>
                      <Badge color={team.is_active ? 'green' : 'gray'}>
                        {team.is_active ? t('Active') : t('Inactive')}
                      </Badge>
                    </div>

                    {/* Members */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
                      <div className="flex items-center gap-1.5 mb-2">
                        <PiUsersLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Members')}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {team.members?.length || 0}
                      </p>
                    </div>

                    {/* Documents */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
                      <div className="flex items-center gap-1.5 mb-2">
                        <PiTagLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Documents')}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {(team as any).document_count || 0}
                      </p>
                    </div>

                    {/* Created */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
                      <div className="flex items-center gap-1.5 mb-2">
                        <PiCalendarBlankLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Created')}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {team.created_at ? formatDate(team.created_at) : '-'}
                      </p>
                    </div>
                  </div>

                  {/* ── Practice Areas Card ── */}
                  {team.practice_areas && team.practice_areas.length > 0 && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
                        <PiTagLight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-xs font-medium text-gray-400 dark:text-gray-500">{t('Practice Areas')}</span>
                      </div>
                      <div className="p-4">
                        <div className="flex flex-wrap gap-2">
                          {team.practice_areas.map((pa) => (
                            <Badge key={pa.id} color="blue">
                              {pa.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Activity Row ── */}
                  {team.created_at && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
                        <PiCalendarBlankLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Created')}</span>
                          <p className="text-sm text-gray-700 dark:text-gray-200">{formatDate(team.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Recent Activity ── */}
                  <RecentActivityCard
                    fetchEntries={async (limit) => {
                      const res: any = await dispatch(fetchEntityAuditLog('Team', teamId, 1, limit) as any);
                      return res?.data || [];
                    }}
                  />
                </div>

                {/* ── Right Column ── */}
                <div className="space-y-5">

                  {/* ── Members Card (read-only list + Manage button) ── */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                    <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
                      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                        <PiUsersLight className="h-4 w-4" />
                        <span className="text-xs font-medium">
                          {t('Members')} ({(team.members || []).length})
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="small"
                        onClick={() => dispatch(showSidePanel({
                          type: 'manageResourceCollaborators',
                          showSidePanel: true,
                          width: '50%',
                          message: t('Manage Members'),
                          subMsg: t('Add or remove members of this team.'),
                          icon: <PiUsersLight className="w-5 h-5" />,
                          resourceUuid: teamId,
                          resourceType: 'team',
                          collaborators: (team.members || []).map((m: any) => ({
                            user_id: m.id,
                            collaborator_uuid: m.uuid || String(m.id),
                            name: m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email || '',
                            email: m.email || '',
                            initials: m.initials || ((m.first_name?.[0] || '') + (m.last_name?.[0] || '')).toUpperCase() || '??',
                            profile_picture: m.profile_picture || null,
                            access: m.role === 'lead' ? 'admin' : (m.role === 'member' ? 'edit' : 'view'),
                            role: m.role,
                            source: 'direct',
                          })),
                          currentUserUuid: userInfo?.UserID ? String(userInfo.UserID) : undefined,
                          canAdmin: true,
                          addCollaborator: async (uuid: string, userUuid: string, _access: string) => {
                            await dispatch(addTeamMember(uuid, userUuid, 'member') as any);
                          },
                          removeCollaborator: async (uuid: string, collabUuid: string) => {
                            await dispatch(removeTeamMember(uuid, collabUuid) as any);
                          },
                          updateAccess: async (_uuid: string, _collabUuid: string, _access: string) => {
                            // Teams don't support per-member access levels yet — no-op.
                            return true;
                          },
                          onUpdate: () => { loadTeam(); },
                        } as any))}
                      >
                        {t('Manage')}
                      </Button>
                    </div>
                    <div className="p-4">
                      {(team.members || []).length === 0 ? (
                        <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
                          {t('No members yet.')}
                        </p>
                      ) : (
                        <ul className="space-y-2.5">
                          {(team.members || []).slice(0, 8).map((m: any, idx: number) => {
                            const name = m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email || '';
                            const initials = m.initials || ((m.first_name?.[0] || '') + (m.last_name?.[0] || '')).toUpperCase() || '??';
                            return (
                              <li key={m.id || idx} className="flex items-center gap-2.5">
                                <ProfileImage
                                  profilePicture={m.profile_picture || null}
                                  email={m.email || ''}
                                  initials={initials}
                                  firstName={m.first_name}
                                  lastName={m.last_name}
                                  size="w-7 h-7"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{name}</p>
                                  {m.email && <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">{m.email}</p>}
                                </div>
                                {m.role === 'lead' ? (
                                  <Badge color="blue">{t('Lead')}</Badge>
                                ) : (
                                  <span className="text-[10px] text-gray-400 dark:text-gray-500">{t('Member')}</span>
                                )}
                              </li>
                            );
                          })}
                          {(team.members || []).length > 8 && (
                            <li className="text-[11px] text-gray-400 text-center pt-1">
                              +{(team.members || []).length - 8} {t('more')}
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'feed' && (
            <div className="flex-1 min-h-0 flex flex-col">
              <FeedComponent
                events={feed}
                loading={feedLoading}
                onRefresh={loadFeed}
                onPostEvent={handlePostFeedEvent}
                onCommentAdd={handleAddComment}
                currentUser={
                  userInfo
                    ? {
                        id: userInfo.UserID || 'current-user',
                        first_name: userInfo.FirstName || 'You',
                        last_name: userInfo.LastName || '',
                        email: userInfo.Email || '',
                      }
                    : undefined
                }
                teamMembers={team?.members || []}
              />
            </div>
          )}

          {activeTab === 'matters' && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              {t('Matters for this team will appear here')}
            </div>
          )}

          {activeTab === 'documents' && (
            <DocumentsTab
              teamId={teamId}
              documents={documents}
              loading={documentsLoading}
              isUploading={isUploadingDocs}
              uploadingCount={uploadingDocCount}
              onUpload={handleUploadDocument}
              onDelete={handleDeleteDocument}
              onBulkDelete={handleBulkDeleteDocuments}
              onRefresh={loadDocuments}
            />
          )}

        </motion.div>
      </div>

      {/* Audit Log Side Panel with Backdrop */}
      <FeatureTour
        steps={teamDetailTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />

      {showAuditLog && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 transition-opacity duration-300"
            onClick={() => setShowAuditLog(false)}
          />
          <div className="fixed inset-y-0 right-0 z-50 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col animate-slide-in-right">
            <AuditLogPanel
              entityType="Team"
              entityId={teamId}
              entityName={team?.name}
              onClose={() => setShowAuditLog(false)}
            />
          </div>
        </>
      )}
    </DetailFrame>
  );
};

export default TeamDetail;
